import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import moment from "moment";
import fetchTeamTable from "hooks/getTeamTable";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import teamCodeHashmap from "utils/teamCodeHashmap";
import appConfig from "appConfig";
import {
  filterChartDataByHistoryRange,
  findWrExtremes,
} from "../../utils/teamHistoryFilter";
import {
  getChartXAxisInterval,
  getChartXAxisTickFormatter,
  getChartXAxisTickProps,
  getExportChartDimensions,
} from "../../utils/chartExportHelpers";
import "assets/styles.css";
import { isMobile } from "react-device-detect";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload;
  if (!point) return null;

  if (point.highlightLabel) {
    return (
      <div className="TDChartTooltip">
        <div>{moment(label).format("DD MMM YYYY")}</div>
        <div>{point.highlightLabel}</div>
      </div>
    );
  }

  return (
    <div className="TDChartTooltip">
      <div>{moment(label).format("DD MMM YYYY")}</div>
      <div>Points: {point.value}</div>
    </div>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string,
};

const RankPointsChart = ({ teamName, apiDate, gender, historyRange, exportMode = false }) => {
  const [rawChartData, setRawChartData] = useState([]);
  const [chartDimensions, setChartDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [error, setError] = useState(null);
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setError(null);

        if (!accounts || accounts.length === 0) {
          throw new Error("No account found");
        }

        const request = {
          scopes: appConfig.authScopes,
          account: accounts[0],
        };

        const accessTokenResponse = await instance.acquireTokenSilent(request);
        const accessToken = accessTokenResponse.accessToken;

        const teamEntry = teamCodeHashmap[teamName.toLowerCase()];
        const teamCode = teamEntry ? Object.keys(teamEntry)[0] : "";

        if (!teamCode) {
          console.error("Team not found");
          return;
        }

        const data = await fetchTeamTable(
          teamCode,
          apiDate,
          gender,
          -1,
          0,
          accessToken
        );
        const sortedData = data
          .filter((item) => {
            const dateValue = moment(item.dateTimeUtc, moment.ISO_8601, true);
            return item.dateTimeUtc && dateValue.isValid();
          })
          .sort(
            (a, b) =>
              moment(a.dateTimeUtc).toDate() - moment(b.dateTimeUtc).toDate()
          );

        const formattedData = sortedData.map((item) => ({
          date: moment(item.dateTimeUtc).format("YYYY-MM-DD"),
          value: parseFloat(item.n_NewWRS_target).toFixed(2),
        }));

        setRawChartData(formattedData);
      } catch (fetchError) {
        if (fetchError instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect({
            scopes: appConfig.authScopes,
            account: accounts[0],
          });
        } else {
          setError(fetchError.message);
        }
      }
    };

    fetchChartData();
  }, [teamName, apiDate, gender, instance, accounts]);

  const chartData = useMemo(
    () => filterChartDataByHistoryRange(rawChartData, historyRange),
    [rawChartData, historyRange]
  );

  const { maxValue, minValue, maxIndex, minIndex } = useMemo(
    () => findWrExtremes(chartData),
    [chartData]
  );

  const chartDataWithHighlights = useMemo(
    () =>
      chartData.map((point, index) => {
        let highlightLabel = null;

        if (index === maxIndex && maxValue != null) {
          highlightLabel = `Max WR: ${point.value}`;
        } else if (index === minIndex && minValue != null) {
          highlightLabel = `Min WR: ${point.value}`;
        }

        return { ...point, highlightLabel };
      }),
    [chartData, maxIndex, minIndex, maxValue, minValue]
  );

  useEffect(() => {
    const updateChartDimensions = () => {
      if (exportMode) {
        setChartDimensions(getExportChartDimensions());
        return;
      }

      const vw = window.innerWidth;
      const isMobileViewport = window.innerWidth <= 768;
      setChartDimensions({
        width: isMobile || isMobileViewport ? 400 : vw * 0.4,
        height: isMobile || isMobileViewport ? 200 : vw * 0.23571,
      });
    };

    updateChartDimensions();
    window.addEventListener("resize", updateChartDimensions);
    return () => window.removeEventListener("resize", updateChartDimensions);
  }, [exportMode]);

  const renderDots = (props) => {
    const { cx, cy, index } = props;

    if (index === maxIndex) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill="#27D477"
          stroke="#27D477"
          strokeWidth={1}
        />
      );
    }

    if (index === minIndex) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill="#E53935"
          stroke="#E53935"
          strokeWidth={1}
        />
      );
    }

    if (chartDataWithHighlights.length <= 20) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill="#FFF"
          stroke="#2732D4"
          strokeWidth={1}
        />
      );
    }

    const interval = Math.floor(chartDataWithHighlights.length / 20);
    if (index % interval === 7) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill="#FFF"
          stroke="#2732D4"
          strokeWidth={1}
        />
      );
    }

    return null;
  };

  const scaledStrokeWidth = Math.max(1, chartDimensions.width / 400);

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!chartDataWithHighlights.length) {
    return (
      <div className="TDChartEmptyState">
        No data available for selected range
      </div>
    );
  }

  const values = chartDataWithHighlights.map((d) => parseFloat(d.value));
  const maxY = Math.max(...values);
  const minY = Math.min(...values);

  const xAxisInterval = getChartXAxisInterval(
    chartDataWithHighlights.length,
    exportMode
  );
  const xAxisTickProps = getChartXAxisTickProps(
    chartDataWithHighlights.length,
    exportMode
  );

  return (
    <div className={exportMode ? "TDChartExportWrapper" : undefined}>
      <LineChart
        width={chartDimensions.width}
        height={chartDimensions.height}
        data={chartDataWithHighlights}
        margin={exportMode ? { bottom: 20, left: 8, right: 16, top: 8 } : undefined}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="none" />
        <XAxis
          className="RankingPointsChartAxis"
          dataKey="date"
          stroke="#221B70"
          tickFormatter={(date) =>
            getChartXAxisTickFormatter(
              date,
              chartDataWithHighlights.length,
              exportMode
            )
          }
          interval={xAxisInterval}
          tick={xAxisTickProps}
          height={exportMode ? 56 : 30}
        />
        <YAxis
          className="RankingPointsChartAxis"
          stroke="#221B70"
          domain={[Math.floor(minY / 50) * 50, Math.ceil(maxY / 50) * 50]}
          tickCount={Math.max(
            2,
            Math.ceil(maxY / 50) - Math.floor(minY / 50) + 1
          )}
          tickFormatter={(tick) => Math.round(tick)}
          padding={{ bottom: 10 }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Line
          type="monotone"
          dataKey="value"
          stroke="#2732D4"
          name="Points"
          strokeWidth={scaledStrokeWidth}
          dot={renderDots}
          activeDot={{ r: 4, fill: "#2732D4", stroke: "#2732D4" }}
        />
      </LineChart>
    </div>
  );
};

RankPointsChart.propTypes = {
  teamName: PropTypes.string.isRequired,
  apiDate: PropTypes.string.isRequired,
  gender: PropTypes.number.isRequired,
  historyRange: PropTypes.shape({
    startYear: PropTypes.number,
    endYear: PropTypes.number,
    endDate: PropTypes.instanceOf(Date),
  }),
  exportMode: PropTypes.bool,
};

RankPointsChart.defaultProps = {
  historyRange: null,
  exportMode: false,
};

export default RankPointsChart;
